import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import ArticleCard from '@/components/base/ArticleCard';
import { allComics } from '@/mocks/comics';
import { articles } from '@/mocks/articles';
import type { Comic } from '@/mocks/comics';
import type { Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const numId = parseInt(id || '0', 10);

  const comic: Comic | undefined = allComics.find((c) => c.id === numId);
  const article: Article | undefined = articles.find((a) => a.id === numId);

  if (!comic && !article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <div className="pt-16 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
              </div>
              <h1 className="text-xl font-bold text-foreground-950 mb-2">找不到该内容</h1>
              <p className="text-foreground-500 mb-6">该漫画或文章不存在或已被移除</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-line"></i> 返回首页
              </Link>
            </div>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  if (comic) {
    const relatedComics = allComics
      .filter((c) => c.category === comic.category && c.id !== comic.id)
      .slice(0, 6);

    const comicSchema = {
      '@context': 'https://schema.org',
      '@type': 'Book',
      name: comic.title,
      author: { '@type': 'Person', name: comic.author },
      description: comic.description,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: comic.rating,
        bestRating: 10,
        ratingCount: parseInt(comic.views.replace('万', '0000'), 10),
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://xiuxiumanhua.com/' },
        { '@type': 'ListItem', position: 2, name: comic.category, item: `https://xiuxiumanhua.com/category/${comic.category}` },
        { '@type': 'ListItem', position: 3, name: comic.title, item: `https://xiuxiumanhua.com/article/${comic.id}` },
      ],
    };

    return (
      <>
        <ScrollToTop />
        <SEOHead
          title={`${comic.title} - 羞羞漫画在线阅读`}
          description={`${comic.title}，${comic.author}作品。${comic.description} 共${comic.chapters}话，${comic.status}，评分${comic.rating}。在羞羞漫画阅读最新章节。`}
          keywords={`${comic.title},${comic.title}在线阅读,${comic.title}最新章节,羞羞漫画,${comic.category}漫画`}
          canonicalUrl={`/article/${comic.id}`}
          ogImage={comic.cover}
          ogType="book"
          schemaData={{ ...comicSchema, ...breadcrumbSchema }}
        />

        <div className="min-h-screen bg-background-50">
          <Navbar />

          <div className="pt-16">
            <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 md:py-14">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link to={`/category/${comic.category}`} className="hover:text-primary-500 transition-colors cursor-pointer">{comic.category}</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-950 font-medium">{comic.title}</span>
              </nav>

              {/* Comic Detail Header */}
              <div className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
                <div className="w-full md:w-56 flex-shrink-0">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-100">
                    <img
                      src={comic.cover}
                      alt={`${comic.title} - 羞羞漫画在线阅读`}
                      title={`${comic.title} - 羞羞漫画最新章节`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                    {comic.title}
                  </h1>
                  <p className="text-sm text-foreground-500 mb-4">作者：{comic.author}</p>

                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      comic.status === '连载中' ? 'bg-accent-100 text-accent-900' : 'bg-secondary-100 text-secondary-900'
                    }`}>
                      {comic.status}
                    </span>
                    {comic.tags.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 rounded-full text-xs bg-background-100 text-foreground-600">{tag}</span>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                    <div className="text-center p-3 rounded-lg bg-background-100">
                      <p className="text-lg font-bold text-primary-500">{comic.rating}</p>
                      <p className="text-[11px] text-foreground-500">评分</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-background-100">
                      <p className="text-lg font-bold text-foreground-950">{comic.chapters}</p>
                      <p className="text-[11px] text-foreground-500">总话数</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-background-100">
                      <p className="text-lg font-bold text-foreground-950">{comic.views}</p>
                      <p className="text-[11px] text-foreground-500">阅读量</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-background-100">
                      <p className="text-lg font-bold text-foreground-950">{comic.updatedAt}</p>
                      <p className="text-[11px] text-foreground-500">最后更新</p>
                    </div>
                  </div>

                  <p className="text-sm text-foreground-600 leading-relaxed mb-6">{comic.description}</p>

                  <div className="flex items-center gap-3 flex-wrap">
                    <Link
                      to="/category/all"
                      className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      开始阅读
                    </Link>
                    <button className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-bookmark-line mr-1.5"></i>加入书架
                    </button>
                    <button className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-share-forward-line mr-1.5"></i>分享
                    </button>
                  </div>
                </div>
              </div>

              {/* Related Comics */}
              {relatedComics.length > 0 && (
                <section>
                  <h2 className="text-lg font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                    同类推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-product-shop>
                    {relatedComics.map((c) => (
                      <Link
                        key={c.id}
                        to={`/article/${c.id}`}
                        className="group block cursor-pointer"
                        data-product-shop
                      >
                        <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100 mb-2">
                          <img
                            src={c.cover}
                            alt={c.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <h3 className="text-xs font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                          {c.title}
                        </h3>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>

          <Footer />
        </div>
      </>
    );
  }

  if (article) {
    const relatedArticles = articles
      .filter((a) => a.id !== article.id)
      .slice(0, 3);

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      author: { '@type': 'Person', name: article.author },
      datePublished: article.date,
      description: article.summary,
      image: article.cover,
      publisher: {
        '@type': 'Organization',
        name: '羞羞漫画',
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://xiuxiumanhua.com/' },
        { '@type': 'ListItem', position: 2, name: '漫画资讯', item: 'https://xiuxiumanhua.com/' },
        { '@type': 'ListItem', position: 3, name: article.title, item: `https://xiuxiumanhua.com/article/${article.id}` },
      ],
    };

    return (
      <>
        <ScrollToTop />
        <SEOHead
          title={`${article.title} - 羞羞漫画资讯`}
          description={article.summary}
          keywords={`${article.tags.join(',')},羞羞漫画,漫画资讯`}
          canonicalUrl={`/article/${article.id}`}
          ogImage={article.cover}
          ogType="article"
          schemaData={{ ...faqSchema, ...breadcrumbSchema }}
        />

        <div className="min-h-screen bg-background-50">
          <Navbar />

          <div className="pt-16">
            <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 md:py-14">
              <div className="max-w-3xl mx-auto">
                {/* Breadcrumb */}
                <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
                  <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                  <span className="text-foreground-950 font-medium">漫画资讯</span>
                  <i className="ri-arrow-right-s-line text-xs"></i>
                  <span className="text-foreground-500 truncate">{article.title}</span>
                </nav>

                {/* Article Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-900">
                      {article.category}
                    </span>
                    {article.tags.map((tag) => (
                      <span key={tag} className="text-xs text-foreground-500">{tag}</span>
                    ))}
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                    {article.title}
                  </h1>
                  <div className="flex items-center gap-4 text-sm text-foreground-500">
                    <span><i className="ri-user-line mr-1"></i>{article.author}</span>
                    <span><i className="ri-calendar-line mr-1"></i>{article.date}</span>
                    <span><i className="ri-eye-line mr-1"></i>{article.views} 阅读</span>
                  </div>
                </div>

                {/* Cover Image */}
                <div className="aspect-[16/9] rounded-xl overflow-hidden bg-background-100 mb-8">
                  <img
                    src={article.cover}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Article Content */}
                <article
                  className="prose prose-sm max-w-none text-foreground-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                {/* Article Footer */}
                <div className="border-t border-background-200/70 mt-10 pt-6">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-4">
                      <button className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                        <i className="ri-thumb-up-line"></i> 点赞
                      </button>
                      <button className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                        <i className="ri-chat-3-line"></i> 评论
                      </button>
                      <button className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                        <i className="ri-share-forward-line"></i> 分享
                      </button>
                    </div>
                    <Link
                      to="/"
                      className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line mr-1"></i>返回首页
                    </Link>
                  </div>
                </div>
              </div>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <div className="max-w-3xl mx-auto mt-10">
                  <h2 className="text-lg font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {relatedArticles.map((a) => (
                      <ArticleCard key={a.id} article={a} compact />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <Footer />
        </div>
      </>
    );
  }

  return null;
}